import { useEffect } from 'react'
import { useLoginFlow, useAllSessionStorage } from '@ztechapps/common.hook'
import { CenterScreen, LoadingContent } from '@ztechapps/webui.util'
import { routesDef } from '../../config/routes'
import { supportRedirectUrlStorageKey } from '../../config/settings'

// Mirrors apps/quickscore/admin/src/view/account/login-success/index.tsx — the landing point
// for the redirect useVerifyCode.ts (passwordless) and BasicLoginForm's native form POST
// (basic) both send the browser to after WebsiteRouter.ts's /auth/passwordless/quickscore-website
// and /auth/basic/quickscore-website mounts authenticate the request. useLoginFlow reads the
// base64 payload off the :auth route param, and its redirect() writes the same
// app-login-token / app-login-userId keys useSupportSession already reads before doing a real
// window.location navigation onward — never a client-side history push, so the freshly-written
// session is picked up on the very next page load rather than relying on any in-memory state.

// Resolves `value` the same way the browser will when `redirect()` below sets
// `window.location.href` (libs/common/hook/src/useLoginFlow/index.ts:44), so a backslash or
// percent-encoded authority form cannot slip a different origin past a string test — follows
// libs/node/core/src/router/auth/BaseAuthRouter.ts's `isSameSiteRedirect`, which resolves against
// a placeholder base and compares origins for the same reason (its doc comment explains why a
// regular expression cannot guarantee this). This client-side check compares against the real
// origin directly rather than a placeholder, since the browser already knows it.
const isSiteRelativePath = (value: string): boolean => {
  if (!value || !value.startsWith('/')) return false
  try {
    return new URL(value, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

export default function AuthSuccessPage() {
  const { redirect, accessToken, redirect_url } = useLoginFlow({ afterLoginPath: routesDef.SupportList.path })
  const sessionStorage = useAllSessionStorage()

  useEffect(() => {
    if (!accessToken) return
    // The passwordless leg cannot carry redirect_url through the server round trip (see
    // SupportSignInPage.tsx), so the payload's own redirect_url may arrive empty even though a
    // return path was captured — fall back to what the sign-in page stashed before sign-in
    // started. Restore only a site-relative path; anything else falls back to the default
    // destination.
    const storedRedirectUrl = sessionStorage.get(supportRedirectUrlStorageKey) as string
    sessionStorage.remove(supportRedirectUrlStorageKey)

    const target = isSiteRelativePath(redirect_url)
      ? redirect_url
      : isSiteRelativePath(storedRedirectUrl)
        ? storedRedirectUrl
        : undefined

    return redirect(target)
  }, [accessToken, redirect, redirect_url, sessionStorage])

  return (
    <CenterScreen>
      <LoadingContent message="Authenticating....." />
    </CenterScreen>
  )
}
